import React from "react";
import { Box, Typography, Grid, useMediaQuery, useTheme } from "@mui/material";

// --- Interfaces ---
export interface TableHeader {
  key: string;
  label: string;
  align?: "left" | "center" | "right";
  width?: string; // Optional column width e.g., '20%'
}

export interface TableRowData {
  [key: string]: string | number | React.ReactNode;
}

interface CustomTableProps {
  headers: TableHeader[];
  rows: TableRowData[];
}

const CustomTable: React.FC<CustomTableProps> = ({ headers, rows }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box sx={{ width: "100%", overflowX: "auto" }}>
      {/* Header */}
      <Grid
        container
        sx={{
          borderBottom: "1px solid #eee",
          pb: 1,
          mb: 1,
          minWidth: "750px",
        }}
        wrap="nowrap"
      >
        {headers.map((header, index) => (
          <Grid
            item
            key={index}
            sx={{
              width: header.width ? header.width : undefined,
              flexBasis: header.width ? header.width : 0,
              flexGrow: header.width ? 0 : 1,
              textAlign: header.align || "left",
              pr: 2,
              minWidth: 0,
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: "#168794",
                textTransform: "uppercase",
                wordBreak: "break-word", // Allow header to wrap
                whiteSpace: "pre-line", // Allow new lines if present
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              {header.label}
            </Typography>
          </Grid>
        ))}
      </Grid>

      {/* Rows */}
      {rows?.map((row, rowIndex) => (
        <Grid
          container
          key={rowIndex}
          sx={{
            py: 1,
            borderBottom: "1px solid #eee",
            minWidth: "750px",
          }}
          wrap="nowrap"
        >
          {headers.map((header, colIndex) => (
            <Grid
              item
              key={colIndex}
              sx={{
                width: header.width ? header.width : undefined,
                flexBasis: header.width ? header.width : 0,
                flexGrow: header.width ? 0 : 1,
                pr: 2,
                textAlign: header.align || "left",
                minWidth: 0,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  color: "#333",
                  wordBreak: "break-word", // Breaks long unbroken words
                  whiteSpace: "pre-line", // Preserves new lines if present
                  fontSize: "0.875rem",
                }}
              >
                {row?.[header.key] ?? "-"}
              </Typography>
            </Grid>
          ))}
        </Grid>
      ))}
    </Box>
  );
};

export default CustomTable;
